import React, { useState, useEffect, useRef } from 'react';
import { addContactMessage, addLaunchUpdate } from '../firebase';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    farmType: '',
    subject: '',
    message: ''
  });

  const [launchEmail, setLaunchEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingLaunch, setIsSubmittingLaunch] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  const [launchStatus, setLaunchStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});

  const messageFormRef = useRef<HTMLDivElement>(null);

  // Handle URL parameters for prefilling form
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSubject = urlParams.get('subject');
    const message = urlParams.get('message');
    const scrollTo = urlParams.get('scrollTo');

    if (urlSubject || message) {
      // Map URL subject text to dropdown values
      let subject = urlSubject || '';
      if (urlSubject === 'Request Demo') {
        subject = 'demo';
      } else if (urlSubject === 'Custom Plugin Development') {
        subject = 'custom-plugin';
      }

      setFormData(prev => ({
        ...prev,
        subject: subject || prev.subject,
        message: message || prev.message
      }));
    }

    // Scroll to the message form if requested
    if (scrollTo === 'message' && messageFormRef.current) {
      setTimeout(() => {
        messageFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({type: null, message: ''});

    const result = await addContactMessage(formData);
    
    if (result.success) {
      setSubmitStatus({type: 'success', message: 'Thank you for your message! We\'ll get back to you within 24 hours.'});
      setFormData({
        name: '',
        email: '',
        farmType: '',
        subject: '',
        message: ''
      });
    } else {
      setSubmitStatus({type: 'error', message: 'There was an error sending your message. Please try again or email us directly.'});
    }
    
    setIsSubmitting(false);
  };

  const handleLaunchSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingLaunch(true);
    setLaunchStatus({type: null, message: ''});

    const result = await addLaunchUpdate(launchEmail);
    
    if (result.success) {
      setLaunchStatus({type: 'success', message: 'You\'re on the list! We\'ll notify you when BarnBills launches.'});
      setLaunchEmail('');
    } else {
      setLaunchStatus({type: 'error', message: 'There was an error signing you up. Please try again.'});
    }
    
    setIsSubmittingLaunch(false);
  };

  // Function to handle quick action button clicks
  const handleQuickAction = (actionType: 'demo' | 'custom') => {
    let subject = '';
    let message = '';

    if (actionType === 'demo') {
      subject = 'demo';
      message = 'I am interested in demoing the BarnBills Dashboard. I am interested mainly in... and I have available from... to...';
    } else if (actionType === 'custom') {
      subject = 'custom-plugin';
      message = 'I am interested in discussing custom plugin development for my horse farm. Specifically, I need...';
    }

    setFormData(prev => ({
      ...prev,
      subject,
      message
    }));

    // Scroll to the message form
    if (messageFormRef.current) {
      messageFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-gray-950 text-white antialiased">
      {/* Hero Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Let's 
            <span className="brand-text-gradient"> Talk</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Ready to revolutionize your horse farm management? Have questions about BarnBills? 
            We'd love to hear from you. Get in touch and let's start the conversation.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>24-hour response time</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Horse industry experts</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>No pressure approach</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div ref={messageFormRef} className="bg-gray-800 border border-gray-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Email Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Farm Type and Subject Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="farmType" className="block text-sm font-medium text-gray-300 mb-2">
                      Farm Type
                    </label>
                    <select
                      id="farmType"
                      name="farmType"
                      value={formData.farmType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select farm type</option>
                      <option value="boarding">Boarding Stable</option>
                      <option value="breeding">Breeding Farm</option>
                      <option value="training">Training Facility</option>
                      <option value="rescue">Rescue/Sanctuary</option>
                      <option value="personal">Personal/Private</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select subject</option>
                      <option value="launch-notification">Get Notified at Launch</option>
                      <option value="demo">Request Demo</option>
                      <option value="pricing">Pricing Questions</option>
                      <option value="features">Feature Questions</option>
                      <option value="custom-plugin">Custom Plugin Development</option>
                      <option value="enterprise">Enterprise Solutions</option>
                      <option value="support">Technical Support</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Tell us about your farm, your needs, or any questions you have..."
                  />
                </div>

                {/* Status Message */}
                {submitStatus.type && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-900/50 border border-green-700 text-green-300' 
                      : 'bg-red-900/50 border border-red-700 text-red-300'
                  }`}>
                    <div className="flex items-center">
                      <svg className={`w-5 h-5 mr-2 ${submitStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`} fill="currentColor" viewBox="0 0 20 20">
                        {submitStatus.type === 'success' ? (
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        )}
                      </svg>
                      <span className="text-sm font-medium">{submitStatus.message}</span>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-6 py-4 rounded-lg font-semibold text-lg transition-all duration-200 ${
                    isSubmitting
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'brand-gradient text-white hover:shadow-lg hover:shadow-blue-500/25'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </div>
                  ) : (
                    'Send Message'
                  )}
                </button>

                {!submitStatus.type && (
                  <p className="text-sm text-gray-400 text-center">
                    We'll get back to you within 24 hours. Usually much sooner.
                  </p>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Get in Touch</h2>
                <p className="text-gray-400 mb-8">
                  Choose the way that works best for you. We're here to help and answer 
                  any questions about BarnBills.
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Email</h3>
                    <p className="text-gray-400 mb-2">Receive email support on becoming a customer</p>
                    <p className="text-gray-300">Use the contact form for fastest response</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Phone</h3>
                    <p className="text-gray-400 mb-2">Available during business hours</p>
                    <p className="text-gray-300">Monday-Friday, 9AM-5PM EST</p>
                    <p className="text-sm text-gray-400 mt-1">Phone number available to customers</p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Location</h3>
                    <p className="text-gray-400 mb-2">Based in the heart of horse country</p>
                    <p className="text-gray-300">Lexington, Kentucky, USA</p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">Business Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Monday - Friday</span>
                    <span className="text-gray-300">9:00 AM - 5:00 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Saturday</span>
                    <span className="text-gray-300">10:00 AM - 2:00 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sunday</span>
                    <span className="text-gray-300">Closed</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-400">
                    <span className="text-orange-400 font-medium">Emergency support</span> available 
                    for critical issues outside business hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">What Can We Help You With?</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Choose the option that best fits your needs. We're here to support you every step of the way.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Get Notified */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM10.5 6.5L15 2l4.5 4.5L15 11 10.5 6.5z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Get Launch Updates</h3>
              <p className="text-gray-400 mb-6">
                Be the first to know when BarnBills launches. Get early access and special pricing.
              </p>
              
              {/* Launch Status Message */}
              {launchStatus.type && (
                <div className={`p-3 rounded-lg mb-4 ${
                  launchStatus.type === 'success' 
                    ? 'bg-green-900/50 border border-green-700 text-green-300' 
                    : 'bg-red-900/50 border border-red-700 text-red-300'
                }`}>
                  <div className="flex items-center justify-center">
                    <svg className={`w-4 h-4 mr-2 ${launchStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      {launchStatus.type === 'success' ? (
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      )}
                    </svg>
                    <span className="text-sm font-medium">{launchStatus.message}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleLaunchSignup} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={launchEmail}
                    onChange={(e) => setLaunchEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmittingLaunch}
                  className={`w-full px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                    isSubmittingLaunch
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'brand-gradient text-white hover:shadow-lg hover:shadow-blue-500/25'
                  }`}
                >
                  {isSubmittingLaunch ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing up...
                    </div>
                  ) : (
                    'Notify Me at Launch'
                  )}
                </button>
              </form>
            </div>

            {/* Request Demo */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Request a Demo</h3>
              <p className="text-gray-400 mb-6">
                See BarnBills in action with a personalized demo tailored to your farm's needs.
              </p>
              <button 
                onClick={() => handleQuickAction('demo')}
                className="border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200"
              >
                Schedule Demo
              </button>
            </div>

            {/* Custom Solutions */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Custom Solutions</h3>
              <p className="text-gray-400 mb-6">
                Need something specific? We build custom plugins and enterprise solutions.
              </p>
              <button 
                onClick={() => handleQuickAction('custom')}
                className="border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200"
              >
                Discuss Custom Work
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-400">
              Quick answers to common questions. Don't see yours? Just ask us directly.
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-3">When will BarnBills be available?</h3>
              <p className="text-gray-400">
                We're currently in beta testing with select customers. Public launch is planned for late 2025. 
                Join our notification list to be among the first to know when we launch.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-3">How much will BarnBills cost?</h3>
              <p className="text-gray-400">
                We're designing flexible pricing that scales with your operation. From small private farms 
                to large commercial operations, we'll have options that work for you. Pricing details will 
                be announced closer to launch.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-3">Can you build custom features for my farm?</h3>
              <p className="text-gray-400">
                Absolutely! We offer custom plugin development and enterprise solutions. Whether you need 
                specialized reporting, integrations, or unique workflows, we can build it for you.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-3">Do you offer training and support?</h3>
              <p className="text-gray-400">
                Yes! We provide comprehensive onboarding, team training, and ongoing support. Our team 
                understands the horse industry and can help you get the most out of BarnBills.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage; 